import { useMemo, useState } from 'react';

import { Input, VSpacing, fromTree, StaticDataFetcherItem, useStaticDataProvider } from '@hh.ru/magritte-ui';
import { Select } from '@hh.ru/magritte-ui-select';
import { BarsOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import getConversionType from 'bloko/common/getConversionType';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { format } from 'bloko/common/trl';

import FilterWrapper from 'src/components/Search/Common/Filters/FilterWrapper';
import FilterTreeSelector from 'src/components/Search/Common/Filters/TreeSelector';
import { useApplyFilter } from 'src/components/Search/Common/Filters/hooks/useApplyFilter';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ClusterKey } from 'src/types/search/common/clusters';
import { CriteriaKey, FilterExpPeriodOption } from 'src/types/search/common/criteria';

const TrlKeys = {
    title: 'employer.resumesSearch.expIndustryWithPeriod.title',
    select: {
        one: 'employer.resumesSearch.expIndustry.select.one',
        some: 'employer.resumesSearch.expIndustry.select.some',
        many: 'employer.resumesSearch.expIndustry.select.many',
        all: 'employer.resumesSearch.expIndustry.select.all',
    },
    modal: {
        title: 'employer.resumesSearch.expIndustry.modal.title',
        save: 'treeselector.save',
        cancel: 'treeselector.cancel',
        placeholder: 'treeselector.quicksearch',
        notFound: 'treeselector.notFound',
    },
};

const immutableInputChange = () => undefined;

const ExpIndustryWithPeriod: TranslatedComponent = ({ trls }) => {
    const [isVisible, setVisible] = useState<boolean>(false);
    const applyFilterIndustry = useApplyFilter(ClusterKey.FilterExpIndustry);
    const applyFilterPeriod = useApplyFilter(ClusterKey.FilterExpPeriodForIndustry);

    const selectedExpIndustries = useSelector(
        (state) => state.searchClusters?.[ClusterKey.FilterExpIndustry].selectedValues
    );
    const { selectedValues: selectedExpPeriod, groups } = useSelector(
        (state) => state.searchClusters?.[ClusterKey.FilterExpPeriodForIndustry]
    );

    const treeItems = useSelector((state) => state.industriesTree);
    const collection = useMemo(() => fromTree(treeItems), [treeItems]);

    const selectedValuesTrl = useMemo(() => {
        if (selectedExpIndustries.length === 0) {
            return trls[TrlKeys.select.all];
        }
        const conversionTrl = getConversionType({
            one: trls[TrlKeys.select.one],
            some: trls[TrlKeys.select.some],
            many: trls[TrlKeys.select.many],
            value: selectedExpIndustries.length,
        });
        return format(conversionTrl, { '{0}': selectedExpIndustries.length });
    }, [trls, selectedExpIndustries]);

    const items: StaticDataFetcherItem[] = useMemo(
        () => Object.values(groups).map((item) => ({ value: item.id, text: item.title })),
        [groups]
    );

    const provider = useStaticDataProvider(items);

    return (
        <FilterWrapper filterKey={ClusterKey.FilterExpIndustry} title={trls[TrlKeys.title]}>
            <Input
                buttonIcon={BarsOutlinedSize24}
                onButtonClick={() => setVisible(true)}
                onFocus={() => setVisible(true)}
                value={selectedValuesTrl}
                onChange={immutableInputChange}
                data-qa="serp__novafilter-exp-industry-select"
            />
            <FilterTreeSelector
                title={trls[TrlKeys.modal.title]}
                isVisible={isVisible}
                setVisible={setVisible}
                collection={collection}
                selectedValues={selectedExpIndustries}
                applyFilter={applyFilterIndustry}
            />
            <VSpacing default={12} />
            {groups && (
                <Select
                    type="radio"
                    name={CriteriaKey.FilterExpPeriodForIndustry}
                    triggerProps={{
                        size: 'medium',
                        label: trls[TrlKeys.title],
                        stretched: true,
                    }}
                    dataProvider={provider}
                    value={items.find((item) => item.value === selectedExpPeriod)}
                    onChange={(newValue) => applyFilterPeriod(newValue.value as FilterExpPeriodOption)}
                    bottomSheetHeight="content"
                />
            )}
        </FilterWrapper>
    );
};

export default translation(ExpIndustryWithPeriod);
